<template>
    <div class="learning-admin-path-dashboard" data-test-view="learning-admin-path-dashboard">
        <l-headline :title="path.name || $t(`learning.admin.path.dashboard.title`)" :tabs="tabs" />

        <div v-if="noData">
            <l-no-data
                :text="
                    path.status !== 'active'
                        ? $t(`learning.admin.path.dashboard.empty.notActive`)
                        : $t(`learning.admin.path.dashboard.empty.noData`)
                "
                :btn-show="rights.L_PATH && path.status !== 'active'"
                :btn-text="$t(`learning.admin.path.dashboard.btn.activate`)"
                :btn-handler="activatePath"
            />
        </div>
        <div v-else>
            <div class="path-stats-count" data-test-id="learning-admin-path-dashboard-stats">
                <div class="path-stats-count-item">
                    <span class="path-stats-count-value color-dark">{{ stats.userCount }}</span>
                    <span class="path-stats-count-label">{{
                        $t("learning.admin.path.dashboard.userCount")
                    }}</span>
                </div>
                <div class="path-stats-count-item">
                    <span class="path-stats-count-value color-dark">{{
                        stats.inProgressCount
                    }}</span>
                    <span class="path-stats-count-label">{{
                        $t("learning.admin.path.dashboard.inProgressCount")
                    }}</span>
                </div>
                <div class="path-stats-count-item">
                    <span class="path-stats-count-value color-dark">
                        {{ Math.round(stats.avgProgress * 100) }}%
                    </span>
                    <span class="path-stats-count-label">{{
                        $t("learning.admin.path.dashboard.avgProgress")
                    }}</span>
                </div>
                <div class="path-stats-count-item">
                    <span class="path-stats-count-value color-dark">{{
                        stats.completedCount
                    }}</span>
                    <span class="path-stats-count-label">{{
                        $t("learning.admin.path.dashboard.completedCount")
                    }}</span>
                </div>
            </div>

            <div
                class="users-added-container"
                data-test-id="learning-admin-path-dashboard-users-added"
            >
                <h4 class="users-added-title color-dark">
                    <strong>{{ $t("learning.admin.path.dashboard.added.title") }}</strong>
                </h4>

                <hr />

                <l-paginate
                    :list="stats.users.added"
                    :items-per-page="5"
                    :enable-scroll-to-top="false"
                    :pagination-top="true"
                    :link-to-route="false"
                    :on-item-click="goToUserProgress"
                    :no-data-text="$t(`learning.admin.path.dashboard.added.empty`)"
                >
                    <template #item="{ item: user }">
                        <l-list-item
                            class="users-added-list-item clickable"
                            data-test-class="users-added-list-item"
                        >
                            <template #title>
                                <div class="users-added-user">
                                    <l-profile-picture
                                        :user="user"
                                        size="s"
                                        class="users-added-user-picture"
                                    />
                                    <span class="users-added-user-name">{{
                                        user.firstname + " " + user.lastname
                                    }}</span>
                                </div>
                            </template>
                            <template #center>
                                <l-progress
                                    :progress-percent="Math.round(user.progressPercentage * 100)"
                                />
                            </template>
                            <template #left>
                                <span class="users-added-date">
                                    <i class="fa fa-calendar"></i
                                    >{{ $t(`learning.admin.path.dashboard.tooltip.addedAt`) }}
                                    {{ user.addedAt | moment("MMM DD, YYYY") }}</span
                                >
                                <span
                                    class="users-added-delete"
                                    data-test-class="users-added-delete"
                                >
                                    <i
                                        v-b-popover.hover.top="{
                                            content: $t(
                                                `learning.admin.path.dashboard.tooltip.delete`,
                                            ),
                                        }"
                                        class="fa fa-trash clickable color-primary"
                                        tabindex="0"
                                        @click.stop="goToRemoveUser(user)"
                                        @keyup.enter.stop="goToRemoveUser(user)"
                                    ></i>
                                </span>
                            </template>
                        </l-list-item>
                    </template>
                </l-paginate>
            </div>

            <div
                class="users-targeted-container"
                data-test-id="learning-admin-path-dashboard-users-targeted"
            >
                <h4 class="color-dark">
                    <strong>{{ $t("learning.admin.path.dashboard.targeted.title") }}</strong>
                </h4>

                <hr />

                <l-paginate
                    :list="stats.users.targeted"
                    :items-per-page="5"
                    :enable-scroll-to-top="false"
                    :pagination-top="true"
                    :link-to-route="false"
                    :on-item-click="goToUserProfile"
                    :no-data-text="$t(`learning.admin.path.dashboard.targeted.empty`)"
                >
                    <template #item="{ item: user }">
                        <l-list-item class="users-targeted-list-item clickable">
                            <template #title>
                                <div class="users-targeted-user">
                                    <l-profile-picture
                                        :user="user"
                                        size="s"
                                        class="users-targeted-user-picture"
                                    />
                                    <span class="users-targeted-user-name">{{
                                        user.firstname + " " + user.lastname
                                    }}</span>
                                </div>
                            </template>

                            <template #left>
                                <span v-show="user.addedAt" class="users-targeted-date"
                                    ><i class="fa fa-calendar-times"></i
                                    >{{ user.addedAt | moment("MMM DD, YYYY") }}</span
                                >
                            </template>
                        </l-list-item>
                    </template>
                </l-paginate>
            </div>
        </div>

        <l-modal v-show="showModal" size="m" :close-on-backdrop-click="false" @close="goBack">
            <template #header>{{ modalTitle }}</template>
            <template #body>
                <router-view></router-view>
            </template>
        </l-modal>
    </div>
</template>

<script>
import { mapGetters } from "vuex";

import api from "@/api";
import LHeadline from "@/components/LHeadline";
import LListItem from "@/components/LListItem";
import LModal from "@/components/LModal";
import LNoData from "@/components/LNoData";
import LPaginate from "@/components/LPaginate";
import LProfilePicture from "@/components/LProfilePicture";
import LProgress from "@/components/LProgress";

export default {
    components: {
        LHeadline,
        LListItem,
        LModal,
        LNoData,
        LPaginate,
        LProfilePicture,
        LProgress,
    },
    props: {
        pathId: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            showModal: false,
            modalTitle: "",
        };
    },
    computed: {
        ...mapGetters({
            rights: "users/current/rights",
            usersSearchList: "users/search/list",
            teamsSearchList: "teams/search/list",
            getPath: "learning/path/details",
            getStats: "learning/path/stats",
        }),
        tabs() {
            return [
                {
                    label: this.$t("learning.admin.path.dashboard.btn.export"),
                    handler: this.downloadExport,
                    openNewWindow: true,
                    iconBeforeLabel: true,
                    show: true,
                    tabClass: "hidden-xs",
                    testId: "learning-admin-path-dashboard-export-progress",
                },
                {
                    tabClass: "space hidden-xs",
                    show: true,
                },
                {
                    label: this.$t("learning.admin.path.dashboard.btn.add"),
                    handler: this.goToAddUser,
                    show: this.rights.L_PATH,
                    tabClass: "btn-primary",
                    testId: "learning-admin-path-dashboard-add-user",
                    isButton: true,
                },
            ];
        },
        path() {
            return this.getPath(this.pathId);
        },
        stats() {
            return this.getStats(this.pathId);
        },
        noData() {
            return !(this.stats?.users?.added?.length || this.stats?.users?.targeted?.length);
        },
    },
    watch: {
        $route: {
            immediate: true,
            handler(newRoute) {
                this.showModal = newRoute.meta && newRoute.meta.modal;
                this.modalTitle = this.getModalTitleFromRoute(newRoute);
            },
        },
    },
    created() {
        this.$emit("new", false);
    },
    methods: {
        goBack() {
            this.$router.push(`/learning/admin/path/${this.pathId}/dashboard`);
        },
        goToEdit() {
            this.$router.push(`/learning/admin/path/${this.pathId}/edit`);
        },
        goToAddUser() {
            this.$router.push(`/learning/admin/path/${this.pathId}/dashboard/users/add`);
        },
        goToRemoveUser(user) {
            this.$router.push(
                `/learning/admin/path/${this.pathId}/dashboard/users/${user._id}/remove/${user.progressId}`,
            );
        },
        goToUserProgress(user) {
            this.$router.push(
                `/learning/admin/path/${this.pathId}/dashboard/users/${user._id}/progress/${user.progressId}`,
            );
        },
        goToUserProfile(user) {
            this.$router.push(`/profile/${user._id}`);
        },
        async downloadExport() {
            const res = await api.get(`/api/learning/path/${this.pathId}/export/init`);
            window.location = res.data.url;
        },
        getModalTitleFromRoute(route) {
            switch (route.name) {
                case "LearningAdminPathDashboardUserAdd":
                    return this.$t("learning.admin.path.dashboard.addUsers.title", {
                        path: this.path.name,
                    });
                case "LearningAdminPathDashboardUserProgress": {
                    const user = this.stats.users.added.find(
                        user => user._id === route.params.userId,
                    );
                    return this.$t("learning.admin.path.dashboard.progress.title", {
                        name: user.firstname,
                        path: this.path.name,
                    });
                }
                case "LearningAdminPathDashboardUserRemove": {
                    const user = this.stats.users.added.find(
                        user => user._id === route.params.userId,
                    );
                    return this.$t("learning.admin.path.dashboard.removeUser.title", {
                        name: user.firstname,
                        path: this.path.name,
                    });
                }
                default:
                    return this.$t("modal.title");
            }
        },
        async activatePath() {
            const version = this.path.version == null ? 0 : this.path.version + 1;

            const path = {
                ...this.path,
                access: {
                    ...this.path.access,
                    targeting: {
                        ...this.path.access.targeting,
                        users: {
                            ...this.path.access.targeting.users,
                            include: this.path.access.targeting.users.include.map(({ _id }) => _id),
                            exclude: this.path.access.targeting.users.exclude.map(({ _id }) => _id),
                        },
                        teams: {
                            ...this.path.access.targeting.teams,
                            include: this.path.access.targeting.teams.include.map(({ _id }) => _id),
                            exclude: this.path.access.targeting.teams.exclude.map(({ _id }) => _id),
                        },
                    },
                },
                version,
                status: "active",
            };

            await this.$store.dispatch({
                type: "learning/path/update",
                payload: { path },
            });

            this.$router.push(`/learning/admin/path`);
        },
    },
};
</script>

<style scoped>
.path-stats-count {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    grid-column-gap: 10px;
    justify-items: center;
    align-items: center;
    justify-content: space-between;
    align-content: space-between;
}
.path-stats-count-item {
    display: flex;
    flex-direction: column;
    align-items: center;

    width: 100%;
    overflow: hidden;
}
.path-stats-count-value {
    font-size: 30px;
    line-height: 1.1;
    margin: 2rem 0 1.5rem;
}
.path-stats-count-label {
    font-size: 18px;
    font-weight: 300;
    line-height: 1.1;
    margin: 2rem 0 1.5rem;
    margin-top: 0;
}

.users-added-container,
.users-targeted-container {
    margin-top: 50px;
}

.users-added-container >>> .list-item {
    justify-content: center;
}
.users-added-container >>> .list-item-right,
.users-added-container >>> .list-item-left {
    width: 27vw;
}
.users-added-container >>> .list-item-left {
    text-align: right;
}
.users-added-container >>> .list-item-center {
    flex-grow: 1;
    text-align: center;
    padding: 0 16px;
}

.users-added-user,
.users-targeted-user {
    display: flex;
    align-items: center;
}
.users-added-user-name,
.users-targeted-user-name {
    margin-left: 10px;
}
.users-added-date,
.users-targeted-date {
    margin-right: 36px;
}
.users-added-date i,
.users-targeted-date i {
    margin-right: 8px;
}
</style>
